import { PlusIcon, SmilePlusIcon } from 'lucide-react';

import { Suspense, lazy, useEffect, useRef, useState } from 'react';

import { useTranslation } from '@splitfiesta/i18n';
import { Button, Card } from '@splitfiesta/ui';

import { useEffectiveTheme } from '@/stores/uiStore';

import { Portal } from './Portal';

// Lazy import for emoji-mart
const EmojiMartPicker = lazy(() => import('./EmojiMartPicker'));

// Loading fallback component
const EmojiPickerLoader = () => (
  <div className="p-8 text-center">
    <div className="loading-spinner mx-auto mb-2"></div>
    <p className="text-sm text-gray-500 dark:text-gray-400">
      Loading emojis...
    </p>
  </div>
);

interface EmojiPickerProps {
  onSelect: (emoji: string) => void;
  className?: string;
  disabled?: boolean;
  existingReactions?: string[];
}

// Quick reactions bar - chosen for common expense reactions
const QUICK_REACTIONS = ['👍', '❤️', '😂', '😱', '🤔', '😭', '🔥'];

export function EmojiPicker({
  onSelect,
  className = '',
  disabled = false,
  existingReactions = [],
}: EmojiPickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [showFullPicker, setShowFullPicker] = useState(false);
  const [buttonPosition, setButtonPosition] = useState({ top: 0 });
  const pickerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { t } = useTranslation();
  const effectiveTheme = useEffectiveTheme();

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setButtonPosition({ top: rect.top });
    }
  }, [isOpen]);

  const handleEmojiSelect = (emoji: string) => {
    if (disabled) return;

    // If user already reacted with this emoji, just close the picker
    if (existingReactions.includes(emoji)) {
      setIsOpen(false);
      setShowFullPicker(false);
      return;
    }

    onSelect(emoji);
    setIsOpen(false);
    setShowFullPicker(false);
  };

  const handleEmojiMartSelect = (emoji: {
    native?: string;
    shortcodes?: string;
  }) => {
    if (disabled) return;

    const emojiChar = emoji.native || emoji.shortcodes || '';

    // If user already reacted with this emoji, just close the picker
    if (existingReactions.includes(emojiChar)) {
      setIsOpen(false);
      setShowFullPicker(false);
      return;
    }

    onSelect(emojiChar);
    setIsOpen(false);
    setShowFullPicker(false);
  };

  const handleShowMore = () => {
    if (disabled) return;
    setShowFullPicker(true);
  };

  return (
    <div className={`relative ${className}`} ref={pickerRef}>
      <Button
        ref={buttonRef}
        variant="ghost"
        size="sm"
        type="button"
        data-testid="emoji-picker-button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className="px-1.5 py-0.5 rounded-full bg-[#F0EFED] dark:bg-gray-700 border border-transparent hover:bg-[#EBEAE8] dark:hover:bg-gray-600 hover:border-gray-300 dark:hover:border-gray-500 h-auto"
        aria-label={t('common:actions.addReaction')}
      >
        <SmilePlusIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
      </Button>

      {isOpen && !showFullPicker && (
        <Portal>
          <div className="fixed inset-0 z-9998">
            {/* Invisible overlay to catch clicks outside */}
            <div
              className="absolute inset-0"
              onClick={() => {
                setIsOpen(false);
                setShowFullPicker(false);
              }}
            />
            {/* Quick reactions popup */}
            <div
              className="absolute left-1/2 transform -translate-x-1/2 z-9999"
              style={{ top: `${buttonPosition.top - 55}px` }}
            >
              <Card className="p-1.5">
                <div className="flex items-center gap-0.5">
                  {QUICK_REACTIONS.map((emoji, index) => (
                    <Button
                      key={`quick-${emoji}-${index}`}
                      variant="ghost"
                      size="sm"
                      type="button"
                      onClick={() => handleEmojiSelect(emoji)}
                      disabled={disabled}
                      className="w-8 h-8 min-w-0 p-0 rounded-sm hover:bg-gray-100 dark:hover:bg-gray-700"
                      aria-label={t('common:actions.reactWith', { emoji })}
                    >
                      <span className="text-lg leading-none">{emoji}</span>
                    </Button>
                  ))}
                  <div className="w-px h-6 bg-gray-200 dark:bg-gray-600 mx-0.5" />
                  <Button
                    variant="ghost"
                    size="sm"
                    type="button"
                    onClick={handleShowMore}
                    disabled={disabled}
                    className="w-8 h-8 min-w-0 p-0 rounded-sm hover:bg-gray-100 dark:hover:bg-gray-700"
                    aria-label={t('social:reactions.moreReactions')}
                  >
                    <PlusIcon className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                  </Button>
                </div>
              </Card>
            </div>
          </div>
        </Portal>
      )}

      {isOpen && showFullPicker && (
        <Portal>
          <div className="fixed inset-0 z-9998">
            {/* Invisible overlay to catch clicks outside */}
            <div
              className="absolute inset-0"
              onClick={() => {
                setIsOpen(false);
                setShowFullPicker(false);
              }}
            />
            {/* Full emoji picker popup */}
            <div
              className="absolute left-1/2 transform -translate-x-1/2 z-9999"
              style={{ top: `${Math.max(buttonPosition.top - 450, 20)}px` }}
            >
              <Card>
                <Suspense fallback={<EmojiPickerLoader />}>
                  <EmojiMartPicker
                    onEmojiSelect={handleEmojiMartSelect}
                    theme={effectiveTheme}
                  />
                </Suspense>
              </Card>
            </div>
          </div>
        </Portal>
      )}
    </div>
  );
}
